'use client';
import { Fragment, Suspense } from 'react';
import BlogHero from './BlogHero';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import Link from 'next/link';
import blogsData from '@/textContent/blogs/blogsData.json';

let blogSlugs = [];
export async function generateStaticParams() {
    blogSlugs = blogsData.map((blog) => ({
        slug: String(blog.heading.toLowerCase().split(' ').join('-')),
    }));

    return blogSlugs;
}

function BlogPage({ post, type, params }) {
    return (
        <Fragment>
            <BlogHero
                title={post.title}
                tag={post.tag}
                date={post.published_on}
                image={`${process.env.NEXT_PUBLIC_API_ENDPOINT}${post.meta_image}`}
                author={{
                    name: post.blogger_data.full_name,
                    designation: post.blogger_data.designation,
                    image: post.blogger_data.avatar,
                }}
                readTime={post.read_time}
                type={type}
            />
            {post.blog_intro && (
                <p className="max-w-3xl mx-auto px-5 md:px-0 my-8 text-lg md:text-xl text-gray-600 leading-relaxed">
                    {post.blog_intro}
                </p>
            )}
            <Suspense>
                <div
                    className="prose prose-lg max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                />
            </Suspense>

            <div className="my-24 md:px-5 flex justify-center">
                <div className="px-6 py-8 md:px-10 md:py-12 md:w-2/3 rounded-2xl bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 md:flex justify-between items-center shadow-2xl hover:shadow-3xl transition-all duration-500 relative overflow-hidden group">
                    {/* Animated background overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                    {/* Content wrapper */}
                    <div className="relative z-10 w-full md:flex justify-between items-center">
                        {/* Text Section */}
                        <div className="mb-6 md:mb-0 md:pr-8">
                            <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
                                Feeling inspired? Pass it on! ✨
                            </p>
                            <p className="text-base md:text-lg text-white/90 mt-2 font-medium">
                                Share this blog with your network
                            </p>
                        </div>

                        {/* Social Icons Section */}
                        <div className="flex items-center gap-3 md:gap-4">
                            <Link
                                href={`https://twitter.com/intent/tweet?url=https://wahni.io/learn/blogs/${params.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <div className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-full flex items-center justify-center shadow-lg hover:shadow-2xl transform hover:scale-110 hover:-translate-y-2 transition-all duration-300 cursor-pointer group/icon">
                                    <i className="ri-twitter-x-fill text-xl md:text-2xl text-gray-800 group-hover/icon:text-black transition-colors duration-300"></i>
                                </div>
                            </Link>
                            <Link
                                href={`https://wa.me/?text=https://wahni.io/learn/blogs/${params.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <div className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-full flex items-center justify-center shadow-lg hover:shadow-2xl transform hover:scale-110 hover:-translate-y-2 transition-all duration-300 cursor-pointer group/icon">
                                    <i className="ri-whatsapp-fill text-xl md:text-2xl text-green-500 group-hover/icon:text-green-600 transition-colors duration-300"></i>
                                </div>
                            </Link>
                            <Link
                                href={`https://www.linkedin.com/shareArticle?mini=true&url=https://wahni.io/learn/blogs/${params.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <div className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-full flex items-center justify-center shadow-lg hover:shadow-2xl transform hover:scale-110 hover:-translate-y-2 transition-all duration-300 cursor-pointer group/icon">
                                    <i className="ri-linkedin-box-fill text-xl md:text-2xl text-blue-600 group-hover/icon:text-blue-700 transition-colors duration-300"></i>
                                </div>
                            </Link>
                            <Link
                                href={`https://www.facebook.com/sharer/sharer.php?u=https://wahni.io/learn/blogs/${params.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <div className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-full flex items-center justify-center shadow-lg hover:shadow-2xl transform hover:scale-110 hover:-translate-y-2 transition-all duration-300 cursor-pointer group/icon">
                                    <i className="ri-facebook-circle-fill text-2xl md:text-3xl text-blue-500 group-hover/icon:text-blue-600 transition-colors duration-300"></i>
                                </div>
                            </Link>
                        </div>
                    </div>

                    {/* Decorative elements */}
                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                    <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                </div>
            </div>
        </Fragment>
    );
}

export default function BlogPageContent({ post, type, params }) {
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey="6Ld-QI0iAAAAAPW9CwclXHZYoX0H0QQLhqTuc2iQ"
            scriptProps={{
                async: false,
                defer: false,
                appendTo: 'head',
                nonce: undefined,
            }}
        >
            <BlogPage post={post} params={params} type={type} />
        </GoogleReCaptchaProvider>
    );
}
